import type { GridPoint, LevelState, PathCommand } from "./types";
import { GridFlowCoreError } from "./types";
import {
  appendCellToPath,
  clearPathToOrigin,
  findPathCellIndex,
  getBacktrackPoint,
  getCell,
  getPathState,
  pointsEqual,
  restorePathCells,
  startPathAtNode,
  truncatePathFrom
} from "./state";

export class AppendCellCommand implements PathCommand {
  private executed = false;

  public constructor(
    private readonly state: LevelState,
    private readonly color: string,
    private readonly point: GridPoint
  ) {}

  public execute(): void {
    assertNotExecuted(this.executed, "AppendCellCommand");
    appendCellToPath(this.state, this.color, this.point);
    this.executed = true;
  }

  public undo(): void {
    assertExecuted(this.executed, "AppendCellCommand");
    const path = getPathState(this.state, this.color);
    const tail = path.cells[path.cells.length - 1];
    if (!tail || !pointsEqual(tail, this.point)) {
      throw new GridFlowCoreError(
        "COMMAND_UNDO_ORDER",
        "AppendCellCommand must be undone in stack order."
      );
    }
    path.cells.pop();
    const cell = getCell(this.state, this.point);
    if (cell.occupiedByColor === this.color) {
      cell.occupiedByColor = null;
      cell.pathIndex = null;
    }
    path.isLinked = false;
    this.executed = false;
  }
}

export class UndoPathCommand implements PathCommand {
  private executed = false;
  private removed: GridPoint | null = null;

  public constructor(
    private readonly state: LevelState,
    private readonly color: string
  ) {}

  public execute(): void {
    assertNotExecuted(this.executed, "UndoPathCommand");
    const path = getPathState(this.state, this.color);
    if (path.cells.length <= 1) {
      throw new GridFlowCoreError("CANNOT_POP_ORIGIN", `Cannot backtrack ${this.color} origin.`);
    }

    const previousCells = path.cells.map((cell) => ({ ...cell }));
    this.removed = previousCells[previousCells.length - 1] ?? null;
    restorePathCells(this.state, this.color, previousCells.slice(0, -1));
    this.executed = true;
  }

  public undo(): void {
    assertExecuted(this.executed, "UndoPathCommand");
    if (!this.removed) {
      throw new GridFlowCoreError("COMMAND_STATE_MISSING", "UndoPathCommand has no removed cell.");
    }
    appendCellToPath(this.state, this.color, this.removed);
    this.executed = false;
  }
}

export class ClearPathCommand implements PathCommand {
  private executed = false;
  private previousCells: GridPoint[] | null = null;

  public constructor(
    private readonly state: LevelState,
    private readonly color: string
  ) {}

  public execute(): void {
    assertNotExecuted(this.executed, "ClearPathCommand");
    this.previousCells = clearPathToOrigin(this.state, this.color);
    this.executed = true;
  }

  public undo(): void {
    assertExecuted(this.executed, "ClearPathCommand");
    if (!this.previousCells) {
      throw new GridFlowCoreError(
        "COMMAND_STATE_MISSING",
        "ClearPathCommand has no previous path."
      );
    }
    restorePathCells(this.state, this.color, this.previousCells);
    this.executed = false;
  }
}

export class StartPathAtNodeCommand implements PathCommand {
  private executed = false;
  private previousCells: GridPoint[] | null = null;

  public constructor(
    private readonly state: LevelState,
    private readonly color: string,
    private readonly point: GridPoint
  ) {}

  public execute(): void {
    assertNotExecuted(this.executed, "StartPathAtNodeCommand");
    this.previousCells = startPathAtNode(this.state, this.color, this.point);
    this.executed = true;
  }

  public undo(): void {
    assertExecuted(this.executed, "StartPathAtNodeCommand");
    if (!this.previousCells) {
      throw new GridFlowCoreError(
        "COMMAND_STATE_MISSING",
        "StartPathAtNodeCommand has no previous path."
      );
    }
    restorePathCells(this.state, this.color, this.previousCells);
    this.executed = false;
  }
}

export class TruncatePathCommand implements PathCommand {
  private executed = false;
  private previousCells: GridPoint[] | null = null;

  public constructor(
    private readonly state: LevelState,
    private readonly color: string,
    private readonly startIndex: number
  ) {}

  public execute(): void {
    assertNotExecuted(this.executed, "TruncatePathCommand");
    const path = getPathState(this.state, this.color);
    this.previousCells = path.cells.map((cell) => ({ ...cell }));
    truncatePathFrom(this.state, this.color, this.startIndex);
    this.executed = true;
  }

  public undo(): void {
    assertExecuted(this.executed, "TruncatePathCommand");
    if (!this.previousCells) {
      throw new GridFlowCoreError(
        "COMMAND_STATE_MISSING",
        "TruncatePathCommand has no previous path."
      );
    }
    restorePathCells(this.state, this.color, this.previousCells);
    this.executed = false;
  }
}

export class CompositePathCommand implements PathCommand {
  private executed = false;
  private readonly executedCommands: PathCommand[] = [];

  public constructor(public readonly commands: readonly PathCommand[]) {}

  public execute(): void {
    assertNotExecuted(this.executed, "CompositePathCommand");
    this.executedCommands.length = 0;
    try {
      for (const command of this.commands) {
        command.execute();
        this.executedCommands.push(command);
      }
      this.executed = true;
    } catch (error) {
      this.rollbackExecuted();
      throw error;
    }
  }

  public undo(): void {
    assertExecuted(this.executed, "CompositePathCommand");
    this.rollbackExecuted();
    this.executed = false;
  }

  private rollbackExecuted(): void {
    for (const command of [...this.executedCommands].reverse()) {
      command.undo();
    }
    this.executedCommands.length = 0;
  }
}

export class CrossingEraseCommand extends CompositePathCommand {
  public constructor(state: LevelState, color: string, point: GridPoint) {
    const crossingCell = getCell(state, point);
    const crossedColor = crossingCell.occupiedByColor;

    if (crossingCell.hasNode && crossingCell.nodeColor !== color) {
      throw new GridFlowCoreError(
        "FOREIGN_NODE_CROSSING",
        "Cannot cross through another color node."
      );
    }
    if (crossedColor === null || crossedColor === color) {
      throw new GridFlowCoreError(
        "NO_CROSSING_PATH",
        "Crossing erase needs another occupied path."
      );
    }

    const crossedPath = getPathState(state, crossedColor);
    const crossingIndex = findPathCellIndex(crossedPath, point);
    if (crossingIndex <= 0) {
      throw new GridFlowCoreError("CANNOT_CROSS_ORIGIN", "Cannot erase another path origin.");
    }

    super([
      new TruncatePathCommand(state, crossedColor, crossingIndex),
      new AppendCellCommand(state, color, point)
    ]);
  }
}

export function createPathMoveCommand(
  state: LevelState,
  color: string,
  point: GridPoint
): PathCommand {
  const path = getPathState(state, color);
  const backtrackPoint = getBacktrackPoint(path);
  if (backtrackPoint && pointsEqual(backtrackPoint, point)) {
    return new UndoPathCommand(state, color);
  }

  const cell = getCell(state, point);
  if (cell.occupiedByColor !== null && cell.occupiedByColor !== color) {
    return new CrossingEraseCommand(state, color, point);
  }

  return new AppendCellCommand(state, color, point);
}

function assertExecuted(executed: boolean, commandName: string): void {
  if (!executed) {
    throw new GridFlowCoreError("COMMAND_NOT_EXECUTED", `${commandName} has not been executed.`);
  }
}

function assertNotExecuted(executed: boolean, commandName: string): void {
  if (executed) {
    throw new GridFlowCoreError("COMMAND_ALREADY_EXECUTED", `${commandName} is already executed.`);
  }
}
